import "./form-utility.css";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";

import DropDown from "@/components/ui/dropdown";
import Breadcrumb from "@/components/bread-crumb/BreadCrumb";

import { exportToExcel } from "@/components/ui/export-toExcel";
import type { Column, VisibleColumns } from "@/components/ui/export-toExcel";

import type { ReportRow, RequestDataSource, ReportCommandRequest } from "@/models/formUtility";
import { getCommandTextAsync, getFolderAsync, getReportDataAsync } from "@/services/formUtilityService";

const FormUtility: React.FC = () => {
  const [commandTextOptions, setCommandTextOptions] = useState<string[]>(["ALL"]);
  const [folderOptions, setFolderOptions] = useState<string[]>(["ALL"]);

  const [selectedCommandText, setSelectedCommandText] = useState("ALL");
  const [selectedFolder, setSelectedFolder] = useState("ALL");

  const [error, setError] = useState<string>("");

  const [showDataSource, setShowDataSource] = useState(false);

  const [rows, setRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);

  const hasRows = rows.length > 0;
  const canSearch = !loading;
  const canExport = hasRows && !loading;

  const columns: Column<ReportRow>[] = useMemo(
    () => [
      { id: "reportName", label: "Report Name" },
      { id: "reportPath", label: "Report Path" },
      { id: "reportFolder", label: "Report Folder" },
      { id: "dataSource", label: "Data Source" }
    ],
    []
  );

  const visibleColumns: VisibleColumns<ReportRow> = useMemo(
    () => ({
      reportName: true,
      reportPath: true,
      reportFolder: true,
      dataSource: showDataSource
    }),
    [showDataSource]
  );

  const setAxiosError = (e: unknown, fallback: string) => {
    if (axios.isAxiosError(e)) {
      const serverMessage = typeof e.response?.data === "string" ? e.response.data : e.message || "Bad Request";
      setError(serverMessage);
      return;
    }
    setError(e instanceof Error ? e.message : fallback);
  };

  useEffect(() => {
    (async () => {
      try {
        setError("");

        const [cmdRes, folderRes] = await Promise.all([getCommandTextAsync(), getFolderAsync()]);

        const cmds = Array.isArray(cmdRes?.data) ? cmdRes.data : [];
        const folders = Array.isArray(folderRes?.data) ? folderRes.data : [];

        setCommandTextOptions(["ALL", ...cmds.filter((x) => (x ?? "").trim() !== "")]);
        setFolderOptions(["ALL", ...folders.filter((x) => (x ?? "").trim() !== "")]);

      } catch (e: unknown) {
        setAxiosError(e, "Failed to load dropdown/auth data");
      }
    })();
  }, []);

  const handleSearch = async () => {
    const req: RequestDataSource = {
      storedProc: selectedCommandText,
      folder: selectedFolder,
      showDataSources: showDataSource
    };

    try {
      setError("");
      setLoading(true);

      const res = await getReportDataAsync(req);
      const data = Array.isArray(res?.data) ? (res.data as ReportRow[]) : [];

      setRows(data);
    } catch (e: unknown) {
      setAxiosError(e, "Failed to load table data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    exportToExcel({
      data: rows,
      columns,
      visibleColumns,
      fileName: "ExportReportData.xlsx"
    });
  };

  const gridRows = useMemo(() => {
    return rows.map((r, idx) => ({
      id: `${(r as any).reportPath ?? ""}|${(r as any).reportName ?? ""}|${idx}`,
      ...r
    }));
  }, [rows]);

  const gridColumns: GridColDef[] = useMemo(() => {
    return [
      { field: "reportName", headerName: "Report Name", flex: 1, minWidth: 180 },
      { field: "reportPath", headerName: "Report Path", flex: 2, minWidth: 320 },
      { field: "reportFolder", headerName: "Report Folder", flex: 1, minWidth: 180 },
      { field: "dataSource", headerName: "Data Source", flex: 1, minWidth: 220 }
    ];
  }, []);

  return (
    <>
      <div className="page-container form-utility-page">
        <div className="card-container">
          <div className="top-row">
            <div className="breadcrumb-area">
              <Breadcrumb
                items={[
                  { label: "Home", path: "/" },
                  { label: "Form Utility", path: "/form-utility" }
                ]}
              />
            </div>
          </div>

          <div className="card-header">
            <h2>Form Utility</h2>
            <p className="subtitle">Select Stored Procedure and Folder, then search and export results.</p>
          </div>

          {error ? <div className="err-text">{error}</div> : null}

          <div className="fu-layout">
            <div className="fu-left">
              <div className="card-section">
                <h3 className="section-title">Search</h3>

                <div className="fu-field">
                  <label className="fu-label">Stored Procedures</label>
                  <div className="fu-dd">
                    <DropDown
                      elementId="storedProcedure"
                      label=""
                      options={commandTextOptions}
                      selectedVal={selectedCommandText}
                      onChange={setSelectedCommandText}
                    />
                  </div>
                </div>

                <div className="fu-field">
                  <label className="fu-label">Folder</label>
                  <div className="fu-dd">
                    <DropDown
                      elementId="folder"
                      label=""
                      options={folderOptions}
                      selectedVal={selectedFolder}
                      onChange={setSelectedFolder}
                    />
                  </div>
                </div>

                <label className="fu-check">
                  <input type="checkbox" checked={showDataSource} onChange={(e) => setShowDataSource(e.target.checked)} />
                  <span>Show Data Sources</span>
                </label>

                <div className="fu-btns">
                  <button className="btn primary" type="button" disabled={!canSearch} onClick={() => void handleSearch()}>
                    {loading ? "Searching..." : "Search"}
                  </button>

                  <button className="btn success" type="button" disabled={!canExport} onClick={handleExport}>
                    Export
                  </button>
                </div>
              </div>
            </div>

            <div className="fu-right">
              {hasRows ? (
                <div className="card-section light">
                  <h3 className="section-title">Results</h3>

                  <div className="fu-grid-wrap">
                    <DataGrid
                      rows={gridRows}
                      columns={gridColumns}
                      loading={loading}
                      disableRowSelectionOnClick
                      rowHeight={34}
                      columnHeaderHeight={36}
                      initialState={{
                        pagination: { paginationModel: { page: 0, pageSize: 50 } }
                      }}
                      pageSizeOptions={[25, 50, 100]}
                      columnVisibilityModel={{ dataSource: showDataSource }}
                      sx={{
                        border: "1px solid #e5e7eb",
                        borderRadius: "10px",
                        background: "#ffffff",
                        "& .MuiDataGrid-columnHeaders": {
                          backgroundColor: "#f8fafc",
                          fontWeight: 800
                        },
                        "& .MuiDataGrid-cell": {
                          fontSize: "13px",
                          color: "#111827"
                        }
                      }}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div style={{ height: 4 }} />
        </div>
      </div>
    </>
  );
};

export default FormUtility;